// FileUpload.tsx ---------------------------------------------------------------------------------
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type UploadableFile = File;

interface FileUploadProps {
  attachments: UploadableFile[];
  setAttachments: React.Dispatch<React.SetStateAction<UploadableFile[]>>;
  multiple?: boolean; // إضافة prop للتحكم في تعدد الملفات
}

const FileUpload: React.FC<FileUploadProps> = ({ attachments, setAttachments, multiple = true }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      if (multiple) {
        // إذا كان multiple مفعلاً، أضف الملفات الجديدة إلى القائمة
        setAttachments(prev => [...prev, ...newFiles]);
      } else {
        // إذا كان multiple غير مفعل، استبدل الملفات بملف واحد فقط
        setAttachments(newFiles.slice(0, 1));
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      const newFiles = Array.from(e.dataTransfer.files);
      if (multiple) {
        // إذا كان multiple مفعلاً، أضف الملفات الجديدة إلى القائمة
        setAttachments(prev => [...prev, ...newFiles]);
      } else {
        // إذا كان multiple غير مفعل، استبدل الملفات بملف واحد فقط
        setAttachments(newFiles.slice(0, 1));
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  // Function to get file icon based on file type or extension
  const getFileIcon = (file: UploadableFile) => {
    const name = 'name' in file ? file.name : '';
    const extension = name?.split('.').pop()?.toLowerCase();

    if ('url' in file && /\.(jpe?g|png|gif|webp|bmp)$/i.test(name)) {
      return (
        <img
          src={(file as { url: string }).url}
          alt={name}
          className='img-thumbnail me-2'
          style={{ width: '35px', height: '35px', objectFit: 'cover' }}
        />
      );
    }

    if (file instanceof File && file.type.startsWith('image')) {
      return (
        <img
          src={URL.createObjectURL(file)}
          alt={file.name}
          className='img-thumbnail me-2'
          style={{ width: '35px', height: '35px', objectFit: 'cover' }}
        />
      );
    }

    switch (extension) {
      case 'pdf':
        return <i className='bi bi-file-earmark-pdf-fill fs-4 me-2 text-danger'></i>;
      case 'doc':
      case 'docx':
        return <i className='bi bi-file-earmark-word-fill fs-4 me-2 text-primary'></i>;
      case 'xls':
      case 'xlsx':
        return <i className='bi bi-file-earmark-excel-fill fs-4 me-2 text-success'></i>;
      case 'ppt':
      case 'pptx':
        return <i className='bi bi-file-earmark-ppt-fill fs-4 me-2 text-warning'></i>;
      case 'zip':
      case 'rar':
        return <i className='bi bi-file-earmark-zip-fill fs-4 me-2 text-secondary'></i>;
      default:
        return <i className='bi bi-file-earmark-fill fs-4 me-2 text-muted'></i>;
    }
  };

  // console.log(attachments);

  return (
    <>
      <div
        className={`border-2 border-dashed text-center p-4 ${
          isDragging ? 'bg-theme bg-opacity-20 border-primary' : ''
        }`}
        style={{ borderColor: '#ccc', cursor: 'pointer' }}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type='file'
          className='d-none'
          ref={fileInputRef}
          multiple={multiple} // التحكم في خاصية multiple في عنصر input
          accept='image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar'
          onChange={handleFileChange}
        />

        <div className='d-flex flex-column align-items-center'>
          <i className='bi bi-cloud-arrow-up-fill fs-1 fa-lg mb-2 text-theme'></i>
          {/* <p className='mb-1'>
            اسحب {multiple ? 'الملفات' : 'الملف'} هنا أو{' '}
            <span className='text-theme text-decoration-underline'>اختر {multiple ? 'ملفات' : 'ملف'}</span>
          </p> */}
          <p className='mb-1'>
            {t('DragFilesHere')} <span className='text-theme text-decoration-underline'>{t('ChooseFiles')}</span>
          </p>
        </div>

        {attachments.length > 0 && (
          <div className='mt-4'>
            <ul className='list-group'>
              {attachments.map((file, i) => (
                <li
                  key={i}
                  className='list-group-item d-flex justify-content-between align-items-center border py-0 px-1'
                  onClick={e => e.stopPropagation()}
                >
                  <div className='d-flex align-items-center'>
                    {getFileIcon(file)}
                    {'url' in file ? (
                      <a
                        href={(file as { url: string }).url}
                        target='_blank'
                        rel='noopener noreferrer'
                        onClick={e => e.stopPropagation()}
                        className='text-truncate text-decoration-none'
                        style={{ maxWidth: '200px' }}
                      >
                        {file.name}
                      </a>
                    ) : (
                      <span className='text-truncate' style={{ maxWidth: '200px' }}>
                        {file.name}
                      </span>
                    )}
                  </div>
                  <span
                    className=' '
                    style={{ cursor: 'pointer' }}
                    onClick={e => {
                      e.stopPropagation();
                      setAttachments(attachments.filter(f => f !== file));
                    }}
                  >
                    <i className='fas fa-trash fa-lg text-danger'></i>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <style>{`
        .border-dashed {
          border-style: dashed;
        }
        .list-group-item {
          background-color: transparent;
          border: none;
          border-bottom: 1px solid #eee;
          padding: 0.5rem 0;
        }
        .list-group {
          max-height: 200px;
          overflow-y: auto;
        }
        div[class*="border-dashed"]:hover {
          background-color: rgba(var(--bs-theme-rgb), 0.1);
          border-color: var(--bs-theme) !important;
        }
        div[class*="border-dashed"]:hover .text-theme {
          color: var(--bs-theme) !important;
        }
      `}</style>
    </>
  );
};

export default FileUpload;
